import { useMemo } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import type { ForecastReportAnalytics } from "../models/ForecastReport";
import { MONTH_LABELS } from "./ForecastReportChart";

const RIGHT_CELL = { textAlign: "right" } as const;
const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const thousandsFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const formatDecimal = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return thousandsFormatter.format(n);
};

// currentMonth is the "YYYY-MM-DD" sent to the procedure, read as text
const emptySamePeriodReason = (
	row: ForecastReportAnalytics,
	currentYear: number,
): string => {
	if (row.ProductionYear > currentYear) return "No completed month yet";
	if (row.LastYearActualProduction === null)
		return `No ${row.ProductionYear - 1} actual production to compare with`;
	if (row.ProductionYear === currentYear) return "No completed month yet";
	return "No completed months with actual production in both years";
};

const samePeriodTooltip = (
	currentMonth: string,
	row?: ForecastReportAnalytics,
): string => {
	if (!row) return "";
	const currentYear = Number(currentMonth.slice(0, 4));
	if (
		row.SamePeriodIncrease === null ||
		row.SamePeriodMonths === null ||
		row.SamePeriodLastMonth === null
	) {
		return emptySamePeriodReason(row, currentYear);
	}
	const last = MONTH_LABELS[row.SamePeriodLastMonth - 1];
	const span =
		row.SamePeriodMonths !== row.SamePeriodLastMonth
			? `${row.SamePeriodMonths} months through ${last}`
			: row.SamePeriodLastMonth === 1
				? "Jan"
				: `Jan-${last}`;
	const text = `${span} ${row.ProductionYear} vs ${span} ${row.ProductionYear - 1}`;
	if (row.ProductionYear !== currentYear) return text;
	const inProgress = MONTH_LABELS[Number(currentMonth.slice(5, 7)) - 1];
	return `${text} (${inProgress} in progress, excluded)`;
};

const buildColumnDefs = (
	currentMonth: string,
): ColDef<ForecastReportAnalytics>[] => [
	{
		field: "ProductionYear",
		headerName: "Year",
		width: 56,
		sortable: false,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Mean",
		headerName: "Mean",
		flex: 1,
		minWidth: 66,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "Increase",
		headerName: "YOY Delta",
		headerTooltip:
			"Year total (actual, or forecast where actual is missing) vs the previous full year's actual. For the current year it compares a partial year.",
		flex: 1,
		minWidth: 66,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "SamePeriodIncrease",
		headerName: "YTD YOY",
		headerTooltip:
			"Same completed months of both years, actual production only. The month in progress is excluded.",
		flex: 1,
		minWidth: 62,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
		tooltipValueGetter: (p) => samePeriodTooltip(currentMonth, p.data),
	},
	{
		field: "StandardDeviation",
		headerName: "Standard Deviation",
		flex: 1.5,
		minWidth: 72,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "CoefficientOfVariation",
		headerName: "Coeff of Var",
		flex: 1,
		minWidth: 62,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "Range",
		headerName: "Range",
		flex: 1,
		minWidth: 66,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "PRR",
		headerName: "PRR",
		flex: 1,
		minWidth: 56,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
];

interface Props {
	rows: ForecastReportAnalytics[];
	currentMonth: string;
}

export const ForecastReportAnalyticsTable = ({ rows, currentMonth }: Props) => {
	const columnDefs = useMemo(
		() => buildColumnDefs(currentMonth),
		[currentMonth],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<AgGridReact<ForecastReportAnalytics>
				columnDefs={columnDefs}
				rowData={rows}
				rowModelType="clientSide"
				defaultColDef={{
					resizable: true,
					wrapHeaderText: true,
					autoHeaderHeight: true,
				}}
				suppressContextMenu
				suppressCellFocus
				tooltipShowDelay={400}
				headerHeight={32}
				rowHeight={28}
				getRowId={(p) => String(p.data.ProductionYear)}
			/>
		</Box>
	);
};
